/**
 * Schema Attribute Utilities
 *
 * Pure functions for processing, filtering, and sorting schema attributes.
 * Used by GenericEntityUpsertDialog and other form-based components.
 *
 * Note: The API returns attributes with PascalCase (Id, DisplayName, Type, etc.) — see
 * types/SchemaTypes.SchemaAttribute. Those are mapped to a form-friendly shape (name, id, type, …)
 * in schemaUtils (ProcessedSchemaAttribute), EntityEntitlements (buildFormAttributesFrom* →
 * FormAttributeOption), and tier2/tier3. This file's SchemaAttribute is that form/consumer shape:
 * it has `name` (and optional `id`, `label`, etc.) because all producers already map API
 * DisplayName/Id to `name`/`id`. getAttributeName(attr) uses name || id || value so form code
 * works with any of these sources without accepting raw API shape here.
 */

import { EXCLUDED_FORM_FIELDS } from './entityTypeDetection';
import { createEntityRef, isA } from '../types';
import { isARef } from './entityPropertyUtils';

/**
 * Form/consumer attribute shape (not the API type).
 * Attributes passed to form utilities come from ProcessedSchemaAttribute, FormAttributeOption,
 * or tier2/tier3 builders — all of which use `name` (from API DisplayName) and often `id` (from API Id).
 */
export interface SchemaAttribute {
    name?: string;
    id?: string;
    value?: string;
    label?: string;
    type?: string;
    computed?: boolean;
    mutability?: string;
    required?: boolean;
    multiValued?: boolean;
    isReference?: boolean;
    entities?: string[];
    isReferenceTo?: any[];
    subAttributes?: SchemaAttribute[];
    /** When true, field is shown but not editable (e.g. readable but not settable per tier3). */
    readOnly?: boolean;
}

/**
 * Get the display name for a schema attribute
 */
export function getAttributeName(attr: SchemaAttribute): string {
    return attr.name || attr.id || attr.value || '';
}

/**
 * Get the display label for a schema attribute
 */
export function getAttributeLabel(attr: SchemaAttribute): string {
    return attr.label || getAttributeName(attr);
}

/**
 * Normalize attribute name for comparison (strip optional leading "$." so "$.DisplayName" matches "DisplayName").
 * Used for sort order, possessive group detection, and excluded-field checks when tier2 uses $. prefixed ids.
 */
export function getAttributeNameForComparison(name: string): string {
    const s = (name ?? '').toString().trim();
    return s.startsWith('$.') ? s.slice(2) : s;
}

/**
 * Returns true if the given key (e.g. form field name, URL segment) refers to this attribute.
 * Case-insensitive comparison against id and displayName. Use instead of ad-hoc === or .toLowerCase().
 */
export function attributeKeyMatches(attr: SchemaAttribute, key: string): boolean {
    return isA(createEntityRef(attr), key);
}

/**
 * Filter out excluded and read-only fields from schema attributes.
 * Excluded check uses normalized name so "$.Id" and "Id" are both excluded.
 */
export function filterEditableAttributes(attributes: SchemaAttribute[]): SchemaAttribute[] {
    return attributes.filter((attr) => {
        const name = getAttributeName(attr);
        const nameForComparison = getAttributeNameForComparison(name).toLowerCase();
        const isExcluded = EXCLUDED_FORM_FIELDS.has(name) || Array.from(EXCLUDED_FORM_FIELDS).some((ex) => ex.toLowerCase() === nameForComparison);
        const isComputed = attr.computed;

        return !isExcluded && !isComputed;
    });
}

/**
 * Sort schema attributes for form display.
 * Priority: DisplayName first, then Type (RAT), CatalogId (PluginFunction), possessive fields, Group order, Subject, ContentType, Body.
 * All other attributes keep their source order (tier2/schema) instead of being reordered alphabetically.
 */
export function sortAttributesForForm(
    attributes: SchemaAttribute[],
    options: {
        isRequestApprovalType?: boolean;
        isPluginFunction?: boolean;
        isContent?: boolean;
        isGroup?: boolean;
        isSet?: boolean;
    } = {}
): SchemaAttribute[] {
    const { isRequestApprovalType = false, isPluginFunction = false, isContent = false, isGroup = false, isSet = false } = options;

    const withIndex = attributes.map((attr, index) => ({ attr, index }));

    withIndex.sort((a, b) => {
        const aRaw = getAttributeName(a.attr).replace(/\s+/g, '');
        const bRaw = getAttributeName(b.attr).replace(/\s+/g, '');
        const aName = getAttributeNameForComparison(aRaw).toLowerCase();
        const bName = getAttributeNameForComparison(bRaw).toLowerCase();

        // DisplayName always comes first
        if (aName === 'displayname') return -1;
        if (bName === 'displayname') return 1;

        // Type always comes second (creation, editing, viewing for all entity types)
        if (aName === 'type') return -1;
        if (bName === 'type') return 1;

        // PluginFunction: CatalogId above all possessive fields (edit/creates and views)
        if (isPluginFunction) {
            if (aName === 'catalogid') return -1;
            if (bName === 'catalogid') return 1;
        }

        // PSGroup (RequestApprovalType): PossessiveScope, then PossessiveTargetAttribute, then PossessiveActorsAttribute(s)
        if (isRequestApprovalType) {
            if (aName === 'possessivescope') return -1;
            if (bName === 'possessivescope') return 1;
            if (aName === 'possessivetargetattribute') return -1;
            if (bName === 'possessivetargetattribute') return 1;
            if (aName === 'possessiveactorsattribute') return -1;
            if (bName === 'possessiveactorsattribute') return 1;
            if (aName === 'possessiveactorsattributes') return -1;
            if (bName === 'possessiveactorsattributes') return 1;
        }

        // PSGroup (PluginFunction): PossessiveScope, then PossessiveTargetAttribute, then PossessiveActorsAttributes
        if (isPluginFunction) {
            if (aName === 'possessivescope') return -1;
            if (bName === 'possessivescope') return 1;
            if (aName === 'possessivetargetattribute') return -1;
            if (bName === 'possessivetargetattribute') return 1;
            if (aName === 'possessiveactorsattributes') return -1;
            if (bName === 'possessiveactorsattributes') return 1;
        }

        // For Set: FilterScope then Filter (right after each other when present)
        if (isSet) {
            if (aName === 'filterscope') {
                if (bName === 'displayname') return 1;
                if (bName === 'filter') return -1;
                return -1;
            }
            if (bName === 'filterscope') {
                if (aName === 'displayname') return -1;
                if (aName === 'filter') return 1;
                return 1;
            }
            if (aName === 'filter') {
                if (bName === 'filterscope') return 1;
                return -1;
            }
            if (bName === 'filter') {
                if (aName === 'filterscope') return -1;
                return 1;
            }
        }

        // For Group: FilterScope, FilterAttributes, ManagedType, Filter (grouped and ordered), then AccountName, Scope, Type
        if (isGroup) {
            const groupFilterOrder = ['filterscope', 'filterattributes', 'managedtype', 'filter'];
            const aIdx = groupFilterOrder.indexOf(aName);
            const bIdx = groupFilterOrder.indexOf(bName);
            if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
            if (aIdx !== -1) return -1;
            if (bIdx !== -1) return 1;

            if (aName === 'accountname' && bName !== 'displayname') return -1;
            if (bName === 'accountname' && aName !== 'displayname') return 1;

            if (aName === 'scope') {
                if (bName === 'displayname') return 1;
                if (bName === 'accountname') return 1;
                if (bName === 'type') return -1;
                return -1;
            }
            if (bName === 'scope') {
                if (aName === 'displayname') return -1;
                if (aName === 'accountname') return -1;
                if (aName === 'type') return 1;
                return 1;
            }

            if (aName === 'type') {
                if (bName === 'displayname') return 1;
                if (bName === 'accountname') return 1;
                if (bName === 'scope') return 1;
                return -1;
            }
            if (bName === 'type') {
                if (aName === 'displayname') return -1;
                if (aName === 'accountname') return -1;
                if (aName === 'scope') return -1;
                return 1;
            }

            // Legacy* attributes at the bottom, grouped (fixed order among themselves)
            const aLegacy = aName.startsWith('legacy');
            const bLegacy = bName.startsWith('legacy');
            if (aLegacy && !bLegacy) return 1;
            if (!aLegacy && bLegacy) return -1;
            if (aLegacy && bLegacy) {
                const legacyOrder = ['legacyfilter', 'legacyfilterscope', 'legacyfilterattributes', 'legacyfilterfunctions', 'legacyfilterliterals'];
                const aLegacyIdx = legacyOrder.indexOf(aName);
                const bLegacyIdx = legacyOrder.indexOf(bName);
                const aIdx = aLegacyIdx === -1 ? legacyOrder.length : aLegacyIdx;
                const bIdx = bLegacyIdx === -1 ? legacyOrder.length : bLegacyIdx;
                return aIdx - bIdx;
            }
        }

        // Subject, ContentType, Body
        if (aName === 'subject') return -1;
        if (bName === 'subject') return 1;
        if (isContent) {
            if (aName === 'contenttype') return -1;
            if (bName === 'contenttype') return 1;
        }
        if (aName === 'body') return -1;
        if (bName === 'body') return 1;

        // Preserve source order for everything else (no alphabetical reorder)
        return a.index - b.index;
    });

    return withIndex.map(({ attr }) => attr);
}

/**
 * Get boolean attributes from a list of schema attributes
 */
export function getBooleanAttributes(attributes: SchemaAttribute[]): SchemaAttribute[] {
    return attributes.filter((attr) => attr.type === 'boolean');
}

/**
 * Get reference attributes from a list of schema attributes
 */
export function getReferenceAttributes(attributes: SchemaAttribute[]): SchemaAttribute[] {
    return attributes.filter((attr) => attr.isReference === true);
}

/**
 * Get required attributes from a list of schema attributes
 */
export function getRequiredAttributes(attributes: SchemaAttribute[]): SchemaAttribute[] {
    return attributes.filter((attr) => attr.required === true);
}

/**
 * Initialize form data from schema attributes
 */
export function initializeFormData(
    attributes: SchemaAttribute[],
    options: {
        isPolicyRule?: boolean;
        isEditMode?: boolean;
    } = {}
): Record<string, any> {
    const { isPolicyRule = false, isEditMode = false } = options;
    const initialData: Record<string, any> = {};

    // PolicyRule: Set default values for new entities
    if (isPolicyRule && !isEditMode) {
        initialData['AllAttributes'] = true;
        initialData['ReadEntity'] = true;
    }

    // Initialize with empty values for create mode
    attributes.forEach((attr) => {
        const name = getAttributeName(attr);
        if (attr.type === 'boolean') {
            // For PolicyRule, some booleans have special defaults
            if (isPolicyRule && !isEditMode) {
                if (isARef(createEntityRef(name), 'AllAttributes') || isARef(createEntityRef(name), 'ReadEntity')) {
                    initialData[name] = true;
                } else {
                    initialData[name] = false;
                }
            } else {
                initialData[name] = false;
            }
        } else if (attr.multiValued) {
            initialData[name] = [];
        } else {
            initialData[name] = '';
        }
    });

    return initialData;
}

/**
 * Process and filter schema attributes for form use
 */
export function processSchemaAttributesForForm(
    attributes: SchemaAttribute[],
    options: {
        isRequestApprovalType?: boolean;
    } = {}
): SchemaAttribute[] {
    const editableAttributes = filterEditableAttributes(attributes);
    return sortAttributesForForm(editableAttributes, options);
}

/**
 * Map reference attributes to a format suitable for selectors
 */
export function mapReferenceAttributesForSelector(attributes: any[]): SchemaAttribute[] {
    return attributes
        .filter((attr: any) => attr.IsReference === true)
        .map((attr: any) => {
            const entityNames = (attr.Entities || []).map((ent: any) =>
                ent.DisplayName || ent.Id || ent
            ).filter(Boolean);

            return {
                name: attr.DisplayName || attr.Id || '',
                id: attr.Id || attr.DisplayName || '',
                value: attr.Id || attr.DisplayName || '',
                label: attr.DisplayName || attr.Id || '',
                type: attr.Type || 'string',
                required: attr.Required || false,
                multiValued: attr.Array || false,
                isReference: true,
                entities: entityNames,
                isReferenceTo: attr.IsReferenceTo || []
            };
        })
        .filter((attr: SchemaAttribute) => attr.name);
}

